import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, Sparkles, ShieldCheck, 
  CreditCard, Landmark, CheckCircle2,
  Search, Zap, Building2, Scale, DollarSign,
  FileCheck, Shield, FileText, Lock, Globe, Leaf, Award, UserCheck, FileSpreadsheet
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentFeatures, Currency, Language } from '../utils/i18n';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface FranceCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: Language) => void;
  onBack: () => void;
  language: Language;
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: Language) => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const FRANCE_AGENT_IDS = [
  'fr-factur-x-chorus-pro-auditor',
  'fr-sas-sarl-inpi-architect',
  'fr-code-du-travail-urssaf-auditor',
  'fr-ccag-cctp-re2020-inspector',
  'fr-boamp-place-procurement-analyst',
  'fr-pac-rpg-ademe-auditor',
  'fr-baux-commerciaux-architect',
  'fr-cnil-rgpd-compliance-auditor'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Landmark className={className} />;
  return <IconComponent className={className} />;
};

export default function FranceCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'EUR',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: FranceCenterHubViewProps) {
  // Default to native French (FR) with multilingual support
  const [activeLang, setActiveLang] = useState<Language>(language || 'fr');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isFr = activeLang === 'fr';
  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const agents = agentsConfig.filter((a: any) => FRANCE_AGENT_IDS.includes(a.id));

  const categories = [
    { id: 'all', label: isFr ? 'Tous les modules France (8)' : isPl ? 'Wszystkie moduły Francja (8)' : isUk ? 'Усі модулі Франції (8)' : 'All France Modules (8)' },
    { id: 'tax_facturx', label: isFr ? 'Factur-X, Chorus Pro & TVA' : isPl ? 'Factur-X, Chorus Pro & TVA' : isUk ? 'Factur-X, Chorus Pro & TVA' : 'Factur-X, Chorus Pro & TVA' },
    { id: 'corporate_contracts', label: isFr ? 'SAS, SARL & Baux Commerciaux' : isPl ? 'SAS, SARL & Baux Commerciaux' : isUk ? 'SAS, SARL & Baux Commerciaux' : 'SAS, SARL & Baux Commerciaux' },
    { id: 'labor_urssaf', label: isFr ? 'Code du Travail, URSSAF & SIPSI' : isPl ? 'Code du Travail, URSSAF & SIPSI' : isUk ? 'Code du Travail, URSSAF & SIPSI' : 'Labor Code, URSSAF & SIPSI' },
    { id: 'btp_re2020', label: isFr ? 'BTP, CCAG Travaux & RE2020' : isPl ? 'BTP, CCAG & RE2020' : isUk ? 'BTP, CCAG & RE2020' : 'BTP, CCAG & RE2020' },
    { id: 'tenders_boamp', label: isFr ? 'Marchés Publics BOAMP & PLACE' : isPl ? 'Marchés Publics & BOAMP' : isUk ? 'Marchés Publics & BOAMP' : 'Tenders BOAMP & PLACE' },
    { id: 'agro_rgpd', label: isFr ? 'Aides PAC, ADEME & CNIL RGPD' : isPl ? 'PAC, ADEME & CNIL RGPD' : isUk ? 'PAC, ADEME & CNIL RGPD' : 'PAC, ADEME & CNIL RGPD' }
  ];

  const filteredAgents = agents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || name.includes(query) || desc.includes(query);

    if (!matchesSearch) return false;
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'tax_facturx') return ['fr-factur-x-chorus-pro-auditor'].includes(agent.id);
    if (selectedCategory === 'corporate_contracts') return ['fr-sas-sarl-inpi-architect', 'fr-baux-commerciaux-architect'].includes(agent.id);
    if (selectedCategory === 'labor_urssaf') return ['fr-code-du-travail-urssaf-auditor'].includes(agent.id);
    if (selectedCategory === 'btp_re2020') return ['fr-ccag-cctp-re2020-inspector'].includes(agent.id);
    if (selectedCategory === 'tenders_boamp') return ['fr-boamp-place-procurement-analyst'].includes(agent.id);
    if (selectedCategory === 'agro_rgpd') return ['fr-pac-rpg-ademe-auditor', 'fr-cnil-rgpd-compliance-auditor'].includes(agent.id);
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in font-mono text-slate-100 max-w-7xl mx-auto pb-12">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800 p-6 rounded-2xl shadow-xl backdrop-blur-md">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-3 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-all border border-slate-700 cursor-pointer hover:scale-105"
            title={isFr ? 'Retour au Hub Principal' : isPl ? 'Wróć do Centrum' : isUk ? 'Назад до Головного Хабу' : 'Back to Main Hub'}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded flex items-center gap-1.5">
                <span>🇫🇷</span>
                <span>RÉPUBLIQUE FRANÇAISE • CODE DE COMMERCE • CGI • URSSAF</span>
              </span>
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded">
                {isFr ? '8 Modules Opérationnels' : isPl ? '8 Modułów' : isUk ? '8 Модулів' : '8 Modules'}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mt-1 flex items-center gap-2">
              <span>🇫🇷</span>
              <span className="bg-gradient-to-r from-blue-400 via-slate-100 to-rose-400 bg-clip-text text-transparent">
                {isFr ? 'HUB ENTREPRISE & CONFORMITÉ FRANCE' : isPl ? 'CENTRUM OPERACYJNE FRANCJA' : isUk ? 'ФРАНЦУЗЬКИЙ ОПЕРАЦІЙНИЙ ЦЕНТР' : 'FRANCE ENTERPRISE & REGULATORY HUB'}
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              {isFr
                ? "Écosystème souverain d'experts IA pour les entreprises françaises : E-facturation Factur-X & Chorus Pro / PPF, statuts SAS/SARL/SCI pour le Guichet Unique INPI, droit du travail Code du travail & URSSAF, détachement SIPSI, BTP RE2020 & CCAG Travaux, marchés publics BOAMP/PLACE, aides PAC Telepac et Bilan Carbone® ADEME."
                : isPl
                ? 'Dedykowany ekosystem narzędzi dla firm we Francji: e-faktury Factur-X & Chorus Pro / PPF, statuty SAS/SARL do INPI, prawo pracy Code du travail & URSSAF, delegowanie SIPSI, BTP RE2020 & CCAG, przetargi BOAMP i Bilan Carbone ADEME.'
                : isUk
                ? 'Спеціалізований операційний хаб для бізнесу у Франції: електронні інвойси Factur-X & Chorus Pro / PPF, статути SAS/SARL до INPI, трудовий комплаєнс Code du travail & URSSAF, відрядження SIPSI, будівництво RE2020 & CCAG, тендери BOAMP та Bilan Carbone ADEME.'
                : 'Turnkey operating suite for French enterprise compliance: Factur-X & Chorus Pro / PPF e-invoicing, SAS/SARL company incorporation with INPI, Code du travail labor law, URSSAF & SIPSI postings, RE2020 & CCAG construction, BOAMP public tenders, and ADEME carbon audit.'}
            </p>
          </div>
        </div>

        {/* Currency & Language Selectors */}
        <div className="flex items-center gap-2.5 self-end md:self-center flex-wrap">
          {onSelectCurrency && (
            <div className="flex items-center bg-slate-800/90 border border-slate-700 rounded-xl p-1 shadow-inner">
              {(['EUR', 'USD', 'PLN', 'UAH'] as Currency[]).map((cur) => (
                <button
                  key={cur}
                  onClick={() => onSelectCurrency(cur)}
                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    currency === cur
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {cur === 'EUR' ? '€ EUR' : cur === 'USD' ? '$ USD' : cur === 'PLN' ? 'zł PLN' : '₴ UAH'}
                </button>
              ))}
            </div>
          )}

          {onSelectLanguage && (
            <div className="flex items-center bg-slate-800/90 border border-slate-700 rounded-xl p-1 shadow-inner">
              {(['fr', 'en', 'uk', 'pl'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => onSelectLanguage(l)}
                  className={`px-2.5 py-1 text-xs font-bold uppercase rounded-lg transition-all cursor-pointer ${
                    activeLang === l
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {l === 'fr' ? '🇫🇷 FR' : l === 'en' ? '🇬🇧 EN' : l === 'uk' ? '🇺🇦 UK' : '🇵🇱 PL'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* France Hub Pass Banner */}
      <div className="relative bg-gradient-to-r from-blue-950/80 via-slate-900/90 to-rose-950/80 border-2 border-blue-500/40 rounded-2xl p-6 shadow-2xl overflow-hidden backdrop-blur-md">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-rose-500/20 text-rose-300 text-xs font-extrabold uppercase rounded-full border border-rose-500/30 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                <span>FRANCE HUB PASS • ACCÈS TOTAL</span>
              </span>
              <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-extrabold rounded-full border border-emerald-500/30">
                150 {isFr ? 'requêtes/mois' : isPl ? 'zapytań/msc' : isUk ? 'запитів/міс' : 'requests/mo'}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
              {isFr
                ? 'Pass Opérationnel Tout-en-Un pour le Marché Français'
                : isPl
                ? 'Abonament Wszystko-w-Jednym dla Rynku Francuskiego'
                : isUk
                ? 'Єдиний Безлімітний Абонемент для Ринку Франції'
                : 'All-Inclusive Operating Pass for the French Market'}
            </h2>
            <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
              {isFr
                ? "Accès complet et illimité aux 8 modules français : Factur-X / Chorus Pro, Statuts SAS/SARL, Code du Travail & SIPSI, RE2020 BTP, marchés publics BOAMP et Bilan Carbone ADEME avec génération de documents au format Word (.docx)."
                : isPl
                ? 'Dostęp do wszystkich 8 modułów: Factur-X / Chorus Pro, Statuty SAS/SARL, Code du Travail & SIPSI, RE2020 BTP, przetargi BOAMP i Bilan Carbone ADEME z eksportem do Word (.docx).'
                : isUk
                ? 'Повний доступ до всіх 8 модулів: Factur-X / Chorus Pro, Статути SAS/SARL, Code du Travail & SIPSI, RE2020 BTP, держзакупівлі BOAMP та Bilan Carbone ADEME з експортом у Word (.docx).'
                : 'Complete access to all 8 French modules: Factur-X / Chorus Pro, SAS/SARL Charters, Code du Travail & SIPSI, RE2020 BTP, BOAMP tenders, and ADEME Bilan Carbone with Word (.docx) export.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <div className="text-right sm:text-left">
              <div className="text-2xl font-black text-white">€399 <span className="text-xs font-normal text-slate-400">/{isFr ? 'mois' : isPl ? 'msc' : isUk ? 'міс' : 'mo'}</span></div>
              <div className="text-[10px] text-emerald-400 font-bold uppercase">{isFr ? 'Économisez 75% vs prix unitaire' : isPl ? 'Oszczędność 75% vs osobno' : isUk ? 'Економія 75% vs окремо' : 'Save 75% vs separate'}</div>
            </div>
            <button
              onClick={() => handleOpenPayment('hub_pass_fr', isFr ? 'France Hub Pass (Accès Total France)' : isPl ? 'Abonament Francja (France Hub Pass)' : isUk ? 'Абонемент Франція (France Hub Pass)' : 'France Hub Pass (All-Inclusive)', 399, 39)}
              className="px-6 py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-rose-600 hover:from-blue-500 hover:to-rose-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer hover:scale-105"
            >
              <Zap className="w-4 h-4" />
              <span>{isFr ? 'Activer le France Pass' : isPl ? 'Aktywuj France Pass' : isUk ? 'Активувати France Pass' : 'Get France Hub Pass'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search & Categories */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isFr ? 'Rechercher un module français (ex. Factur-X, SAS, URSSAF, RE2020, BOAMP)...' : isPl ? 'Szukaj modułów francuskich (np. Factur-X, SAS, URSSAF, RE2020, BOAMP)...' : isUk ? 'Пошук модулів Франції (напр. Factur-X, SAS, URSSAF, RE2020, BOAMP)...' : 'Search French modules (e.g. Factur-X, SAS, URSSAF, RE2020, BOAMP)...'}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/80 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-700">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30 border border-blue-500'
                  : 'bg-slate-900/60 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent: any) => {
          const localizedName = getLocalizedAgentName(agent, activeLang);
          const localizedDesc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);

          return (
            <div
              key={agent.id}
              className="group relative bg-slate-900/70 hover:bg-slate-900/90 border border-slate-800 hover:border-blue-500/50 rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between shadow-lg hover:shadow-2xl hover:shadow-blue-500/10"
            >
              <div className="space-y-4">
                {/* Card Top */}
                <div className="flex items-start justify-between gap-3">
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl group-hover:scale-110 transition-transform">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-slate-800 text-slate-300 border border-slate-700 rounded">
                    {agent.id.startsWith('fr-ccag') || agent.id.startsWith('fr-pac')
                      ? (isFr ? '€39 • €199/mois' : '€39 • €199/msc')
                      : agent.id.startsWith('fr-sas') || agent.id.startsWith('fr-baux')
                      ? (isFr ? '€9 • €49/mois' : '€9 • €49/msc')
                      : (isFr ? '€19 • €99/mois' : '€19 • €99/msc')}
                  </span>
                </div>

                {/* Title & Desc */}
                <div>
                  <h3 className="text-base font-extrabold text-white group-hover:text-blue-300 transition-colors line-clamp-2">
                    {localizedName}
                  </h3>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed line-clamp-3">
                    {localizedDesc}
                  </p>
                </div>

                {/* Features list */}
                <div className="space-y-1.5 pt-2 border-t border-slate-800/80">
                  {features.slice(0, 3).map((feat: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 text-[11px] text-slate-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-5 mt-4 border-t border-slate-800/80 flex items-center gap-2">
                <button
                  onClick={() => onLaunchAgent(agent.id, activeLang)}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-black uppercase rounded-xl transition-all shadow-md shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer group-hover:scale-[1.02]"
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span>{isFr ? '🚀 Lancer le Module' : isPl ? '🚀 Uruchom Moduł' : isUk ? '🚀 Відкрити Модуль' : '🚀 Launch Module'}</span>
                </button>
                <button
                  onClick={() => handleOpenPayment(
                    agent.id,
                    localizedName,
                    agent.id.startsWith('fr-ccag') || agent.id.startsWith('fr-pac') ? 199 : agent.id.startsWith('fr-sas') || agent.id.startsWith('fr-baux') ? 49 : 99,
                    agent.id.startsWith('fr-ccag') || agent.id.startsWith('fr-pac') ? 39 : agent.id.startsWith('fr-sas') || agent.id.startsWith('fr-baux') ? 9 : 19
                  )}
                  className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl transition-colors border border-slate-700 cursor-pointer"
                  title={isFr ? 'Tarifs et abonnement' : isPl ? 'Cennik i abonament' : isUk ? 'Тарифи та підписка' : 'Pricing & Subscription'}
                >
                  <CreditCard className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Regional Payment Modal */}
      {paymentModalOpen && selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          region="EU"
          language={activeLang}
          defaultPrice={selectedPaymentAgent.price}
          defaultSinglePrice={selectedPaymentAgent.singlePrice}
        />
      )}
    </div>
  );
}
